import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import AiChatApi from '../api/AiChat.api';
import { useAiChatStore } from '../store/aiChat.store';

export default function useAgentSendMessage() {
  const setConversationId = useAiChatStore((state) => state.setConversationId);
  const getConversationId = useAiChatStore((state) => state.getConversationId);
  const addMessage = useAiChatStore((state) => state.addMessage);
  const prependConversation = useAiChatStore((state) => state.prependConversation);
  const [sendChatMessageMutation] = useMutation(AiChatApi.SendChatMessage);

  return useCallback(
    (content: string, documentUrls?: Array<{ url: string; filename: string }>) => {
      const conversationId = getConversationId();
      const clientMessageId = uuidv4();

      // Show user message immediately with documents transformed from documentUrls
      const documents =
        documentUrls?.map((doc, index) => ({
          id: -(index + 1), // Temporary negative ID for optimistic UI
          url: doc.url,
          title: doc.filename,
        })) || [];

      addMessage({ content, role: 'USER', createdAt: new Date().toISOString(), documents });

      sendChatMessageMutation({
        variables: { input: { conversationId, content, documentUrls, clientMessageId } },
      }).then(({ data }) => {
        if (conversationId) return;

        const newConversationId = data?.sendChatMessage.conversationId;

        if (newConversationId) {
          const now = new Date().toISOString();
          // Set conversation ID with null title (will be set by narrator) and current time
          setConversationId(newConversationId, null, now);

          // Add new conversation to sidebar
          prependConversation({ id: newConversationId, title: null, createdAt: now, updatedAt: now, metadata: {} });
        }
      });
    },
    [getConversationId, sendChatMessageMutation, setConversationId, addMessage, prependConversation]
  );
}
